import type { FunctionComponent } from "react";
import { useState } from "react";

import { useDispatch } from "react-redux";
import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { SubNavEducationBar } from "@similarweb/ui-components";

import { toggleEducationBar } from "app/actions/educationBarActions";
import { useEducationBarProminentContext } from "app/components/educationbar/useEducationBarProminentContext";
import { ArticlesService } from "app/components/React/EducationBarContainer/ArticlesService";
import { SubNavEducationBarProminent } from "app/components/sun-nav-education-bar-prominent/SubNavEducationBarProminent";
import { i18nFilter } from "app/filters/i18nFilter";
import type { ProductTours } from "app/services/IntercomProductTourService";
import { showIntercomTour } from "app/services/IntercomProductTourService";
import { EducationalVideoModal } from "pro-features/components/Modals/src/EducationalVideoModal";

import { getEducationData, parsedArticle, useCurrentPageState } from "./education-utils";
import { allTrackers } from "app/services/track/track";

interface IEducationContainer {
    onCloseEducationComponent: () => void;
    isBig?: boolean;
}

const StyledEducationBarWrapper = styled.div`
    & > div {
        background-color: ${colorsPalettes.blue[100]};
    }
`;

export const EducationContainer: FunctionComponent<IEducationContainer> = (props) => {
    const [educationalVideoModalIsOpen, setEducationalVideoModalIsOpen] = useState(false);
    const dispatch = useDispatch();

    const currentPage = useCurrentPageState();

    const educationData = getEducationData(currentPage);
    const videoProps = {
        url: `https://vimeo.com/${educationData?.video}`,
        videoConfig: {
            autoPlay: true,
            width: "100%",
            height: "100%",
        },
    };
    const onVideoClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "click to show video modal");
        setEducationalVideoModalIsOpen(true);
    };

    const onVideoModelCloseClick = () => {
        setEducationalVideoModalIsOpen(false);
        allTrackers.dispatchTrackEvent("Button", "close", "click to close Tutorial Video/close/Tutorial Video");
    };
    const onTourClick = () => {
        allTrackers.dispatchTrackEvent(
            "growth.education.bar.click.tour.button",
            "click",
            "growth.education.bar.click.tour.button",
        );
        showIntercomTour(educationData?.intercom as ProductTours);
    };

    const onGetStartedClick = async () => {
        allTrackers.dispatchTrackEvent("Button", "click", "EducationBanner/open/open guide website analysis");
        await ArticlesService.GetArticleById(educationData?.zendesk as number).then((res) => {
            const parsed = parsedArticle(res);
            dispatch(toggleEducationBar(true, parsed));
        });
    };

    const onCloseEducationBar = () => {
        allTrackers.dispatchTrackEvent("Button", "close", "EducationBanner/Close Banner");
        props.onCloseEducationComponent();
    };

    const showVideoButton = !!educationData?.video;
    const showTourButton = !!educationData?.intercom;
    const showGuideButton = !!educationData?.zendesk;

    const titleText = educationData?.title ? i18nFilter()(educationData?.title) : undefined;
    const titleIconName = educationData?.icon as IconName;

    const showEducation = !!educationData;

    const { isProminentEducationBannerShown } = useEducationBarProminentContext();

    if (!showEducation) {
        return null;
    }

    return (
        <>
            {isProminentEducationBannerShown ? (
                <SubNavEducationBarProminent
                    isBig={props.isBig}
                    onCloseEducationBar={onCloseEducationBar}
                    onWatchVideoClick={showVideoButton ? () => onVideoClick() : undefined}
                    onGetStartedClick={showGuideButton ? () => onGetStartedClick() : undefined}
                    videoButtonText={i18nFilter()("growth.education.bar_prominent.video_button")}
                    guideText={i18nFilter()("growth.education.bar_prominent.guide_text")}
                    guideButtonText={i18nFilter()("growth.education.bar_prominent.guide_button")}
                />
            ) : (
                <StyledEducationBarWrapper>
                    <SubNavEducationBar
                        onCloseEducationBar={onCloseEducationBar}
                        onWatchVideoClick={showVideoButton ? onVideoClick : null}
                        onGetStartedClick={showGuideButton ? onGetStartedClick : undefined}
                        onTourClick={showTourButton ? onTourClick : null}
                        titleText={titleText}
                        titleIconName={titleIconName}
                        videoText={i18nFilter()("education.bar_see.video_button")}
                        helpText={i18nFilter()("education.bar_see.article_button")}
                        tourText={i18nFilter()("education.bar_see.tour_button")}
                        extraMarginBottom={false}
                    />
                </StyledEducationBarWrapper>
            )}
            <EducationalVideoModal
                isOpen={educationalVideoModalIsOpen}
                onCloseClick={onVideoModelCloseClick}
                videoConfig={videoProps}
                shouldCloseOnOverlayClick={true}
            />
        </>
    );
};
